import * as _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import {
    DefaultAssessment,
    DefaultChevron,
    IAssessment,
    IBehavior,
    IChevron,
    IStep,
} from 'src/Definitions';
import {
    AssessmentDto,
    AssessmentProcessDto,
    BehaviorDto,
    Client,
    DashboardObservationStatsDto,
    IAssessmentDto,
    IBranchDto,
    IChevronDto,
    ObserverDto,
    StepDto,
    SwaggerResponse,
} from 'src/generated_client';
import GlobalStore from 'src/stores/GlobalStore';
import DataTableStore from '../DataTableStore';
const ColumnSettingsDto = 'AssessmentDto';
export default class AssessmentsStore {
    @observable
    public isLoading: boolean = false;
    @observable
    public isModalOpen: boolean;
    @observable
    public assessment: IAssessment;
    @observable
    public assessments: IAssessment[];
    @observable
    public selectedAssessment?: IAssessment;
    @observable
    public allObservers: ObserverDto[] = [];
    @observable
    public selectedObservers?: string[] = undefined;
    @observable
    public allRegions: string[] = [];
    @observable
    public selectedRegions?: string[] = undefined;
    @observable
    public allObserverInfo: ObserverDto[];
    @observable
    public chevrons: IChevron[];
    @observable
    public dataTableStore: DataTableStore<IAssessmentDto, IAssessment>;
    @observable
    public dashboardStats: DashboardObservationStatsDto;
    @observable
    public isConfirmDialogOpen: boolean;
    @observable
    public includeCompleted?: boolean;
    @observable
    public includeEnded?: boolean;

    constructor(private globalStore: GlobalStore, private client: Client, private baseUrl: string) {
        this.dataTableStore = new DataTableStore<IAssessmentDto, IAssessment>(
            globalStore,
            ({ filter, page, orderBy, showCompleted, showVisitEnded }) =>
                client.getAllAssessments(
                    this.selectedRegions!.toString(),
                    this.selectedObservers!.toString(),
                    filter,
                    page,
                    undefined,
                    orderBy,
                    showCompleted,
                    showVisitEnded
                ),
            (assessment) => this.setupAssessments(assessment),
            (userId, columns) =>
                client.apiColumnSettingsByUserIdByTypePut(userId, ColumnSettingsDto, columns)
        );
    }

    @action.bound
    public closeDialog() {
        this.isModalOpen = false;
        this.selectedAssessment = undefined;
    }

    @action.bound
    public openConfirmDialog() {
        this.isConfirmDialogOpen = true;
    }

    @action.bound
    public closeConfirmDialog() {
        this.isConfirmDialogOpen = false;
    }

    @action.bound
    public setSelectedObservers(observers: string[]) {
        this.selectedObservers = observers;
        this.dataTableStore.loadData();
    }

    @action.bound
    public setIncludeCompleted(show: boolean) {
        this.includeCompleted = show;
    }

    @action.bound
    public setIncludeEnded(show: boolean) {
        this.includeEnded = show;
    }

    @action.bound
    public setSelectedRegions(regions: string[]) {
        this.selectedRegions = regions;
        this.dataTableStore.loadData();
    }

    public getObservationReportPDF(assessment: IAssessment) {
        let url = this.baseUrl + `/api/assessmentReport/${assessment.id}/pdf`;
        if (assessment.id !== undefined && assessment.id !== null) {
            url = url.replace(/[?&]$/, '');
            window.open(url);
        }
    }

    public getObservationReportExcel(assessment: IAssessment) {
        let url = this.baseUrl + `/api/assessmentReport/${assessment.id}/excel`;
        if (assessment.id !== undefined && assessment.id !== null) {
            url = url.replace(/[?&]$/, '');
            window.open(url);
        }
    }

    public openAffinityCluster(assessment: IAssessment) {
        if (assessment.id !== undefined || assessment.id !== null) {
            this.selectedAssessment = assessment;
            this.globalStore.rerouteToPath('/affinityClusters/' + assessment.id);
        }
    }

    public openAssessmentDashboard(assessment: IAssessment) {
        if (assessment.id !== undefined || assessment.id !== null) {
            this.selectedAssessment = assessment;
            this.globalStore.rerouteToPath('/assessmentDashboard/' + assessment.id);
        }
    }

    @action.bound
    public disableAssessment(assessment: IAssessment) {
        this.isLoading = true;
        return this.client
            .archiveAssessment(assessment.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public markAssessmentCompletion = (
        assessment: IAssessment,
        completeAssessment: boolean,
        overrideUncompletedActionItems?: boolean
    ) => {
        return this.saveAssessment(assessment, completeAssessment, overrideUncompletedActionItems);
    };

    @action.bound
    public getColumnSettingsAndAssessments() {
        if (!this.globalStore.user) {
            return;
        }
        this.isLoading = true;

        const getColumnSettings = this.client.apiColumnSettingsByUserIdByTypeGet(
            this.globalStore.user.id,
            ColumnSettingsDto
        );

        const getAllRegions = this.client.getAllRegionNames().then((response) => {
            runInAction(() => {
                this.allRegions = response.result;
            });
        });

        const getAllChampionRegions = this.client
            .getAllUserChampionRegions(this.globalStore.user.id)
            .then((response) => {
                runInAction(() => {
                    if (this.selectedRegions === undefined) {
                        this.selectedRegions = response.result;
                    }
                });
            });

        const getAllObservers = this.client.getObserversOnExistingAssessments().then((response) => {
            runInAction(() => {
                this.allObservers = response.result;

                let isUserIn = false;
                this.allObservers.forEach((o) => {
                    if (o.observerId === this.globalStore.user!.id) {
                        isUserIn = true;
                    }
                });

                if (isUserIn) {
                    if (this.selectedObservers === undefined) {
                        this.selectedObservers = [this.globalStore.user!.id];
                    }
                }
            });
        });

        return Promise.all([
            getColumnSettings,
            getAllChampionRegions,
            getAllRegions,
            getAllObservers,
        ])
            .then((data) => {
                runInAction(() => {
                    this.dataTableStore.setColumns(data[0].result);
                    const sortedColumn = _.find(this.dataTableStore.columns, (col) => !!col.sort);
                    if (sortedColumn) {
                        this.dataTableStore.setTableOrderBy(sortedColumn.sort);
                    }
                    this.dataTableStore.loadData();
                });
            })
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public getAllAssessments(
        filter = '',
        pageNumber?: number,
        pageSize?: number,
        showCompleted?: boolean,
        showVisitEnded?: boolean
    ) {
        if (!pageNumber) {
            pageNumber = 1;
        }
        if (!pageSize) {
            pageSize = 25;
        }
        if (!showCompleted) {
            showCompleted = false;
        }
        if (!showVisitEnded) {
            showVisitEnded = false;
        }
        this.isLoading = true;
        return this.client
            .getAllAssessments(
                undefined,
                undefined,
                filter,
                pageNumber,
                pageSize,
                undefined,
                showCompleted,
                showVisitEnded
            )
            .then((resp: SwaggerResponse<IAssessmentDto[]>) =>
                runInAction(() => {
                    this.assessments = this.setupAssessments(resp.result);
                })
            );
    }

    @action.bound
    public getAssessmentById(assessmentId: string) {
        this.isLoading = true;
        return this.client
            .getAssessmentById(assessmentId)
            .then((resp: SwaggerResponse<IAssessmentDto>) =>
                runInAction(() => {
                    this.assessment = this.addDefaultFields(resp.result);
                    this.selectedAssessment = this.assessment;
                })
            );
    }

    @action.bound
    public getAssessmentsByTargetBranchId(
        targetBranch: IBranchDto,
        callback: (response: SwaggerResponse<IAssessmentDto[]>) => void
    ) {
        this.isLoading = true;
        return this.client
            .getAssessmentsByTargetBranchId(targetBranch.id!)
            .then(callback)
            .catch(() => {})
            .finally(() => {
                this.isLoading = false;
            });
    }

    @action.bound
    public setSelectedAssessment(assessment: IAssessment | undefined) {
        this.selectedAssessment = assessment;
    }

    @action.bound
    public openDialogWithAssessment(assessment: IAssessment) {
        this.isModalOpen = true;
        this.selectedAssessment = assessment;
    }

    @action.bound
    public recoverAssessment(assessment: IAssessment) {
        this.isLoading = true;
        return this.client
            .reactivateAssessment(assessment.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    @action.bound
    public saveAssessment(
        assessment: IAssessment,
        isCompleted?: boolean,
        overrideUncompletedActionItems?: boolean
    ) {
        this.isLoading = true;
        let promise;
        const assessmentDto = new AssessmentDto(assessment);
        if (assessment.isNew) {
            promise = this.client.createAssessment(assessmentDto);
            this.dataTableStore.clearFilter();
        } else {
            promise = this.client.updateAssessment(
                assessment.id,
                assessmentDto,
                isCompleted,
                overrideUncompletedActionItems
            );
        }
        return promise
            .then(() => {
                this.dataTableStore.loadData();
                this.closeDialog();
            })
            .catch((error) => {
                if (error.status === 400) {
                    this.selectedAssessment = assessment;
                    this.openConfirmDialog();
                }
            })
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                    this.isModalOpen = false;
                })
            );
    }

    private setupAssessments = (assessments: IAssessmentDto[]) => {
        return assessments.map((x) => this.setupAssessmentMenuItems(this.addDefaultFields(x)));
    };

    private addDefaultFields = (assessment: IAssessmentDto): IAssessment => {
        return { ...DefaultAssessment(), ...assessment };
    };

    private setupAssessmentMenuItems = (assessment: IAssessment) => {
        assessment.menuItems = [];
        assessment.menuItems.push({
            name: 'Edit',
            onClick: () => this.openDialogWithAssessment(assessment),
        });
        assessment.menuItems.push({
            name: 'View Dashboard',
            onClick: () => this.openAssessmentDashboard(assessment),
        });
        assessment.menuItems.push({
            disabled: !assessment.completedOn,
            name: 'Affinity Cluster',
            onClick: () => this.openAffinityCluster(assessment),
            tooltip: !assessment.completedOn
                ? 'You must complete the assessment before starting an affinity cluster'
                : undefined,
        });
        assessment.menuItems.push({
            name: 'Print Observations (PDF)',
            onClick: () => this.getObservationReportPDF(assessment),
        });
        assessment.menuItems.push({
            name: 'Print Observations (Excel)',
            onClick: () => this.getObservationReportExcel(assessment),
        });
        if (assessment.completedOn !== undefined) {
            assessment.menuItems.push({
                name: 'Mark as Incomplete',
                onClick: () => this.markAssessmentCompletion(assessment, false),
            });
        } else {
            assessment.menuItems.push({
                name: 'Mark as Complete',
                onClick: () => this.markAssessmentCompletion(assessment, true),
            });
        }
        assessment.menuItems.push({
            isConfirm: true,
            name: 'Delete',
            onClick: () => this.disableAssessment(assessment),
        });
        return assessment;
    };

    @action.bound
    public getAssessmentProcessById(assessmentId: string) {
        this.isLoading = true;
        return this.client
            .getAssessmentProcessById(assessmentId)
            .then((resp: SwaggerResponse<AssessmentProcessDto>) =>
                runInAction(() => {
                    if (resp.result.chevrons) {
                        this.chevrons = this.setupChevrons(resp.result.chevrons);
                    }
                })
            );
    }

    private setupChevrons = (chevrons: IChevronDto[]) => {
        return chevrons.map((x) => this.addDefaultChevronFields(x));
    };

    private addDefaultChevronFields = (chevron: IChevronDto): IChevron => {
        return { ...DefaultChevron(), ...chevron };
    };

    @action.bound
    public saveStep(assessmentId: string, step: IStep) {
        this.isLoading = true;
        let promise;
        const stepDto = new StepDto(step);
        if (step.isNew) {
            promise = this.client.createAssessmentStep(assessmentId, stepDto);
        } else {
            promise = this.client.updateAssessmentStep(step.id, assessmentId, stepDto);
        }
        return promise
            .then(() => {})
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public saveBehavior(assessmentId: string, behavior: IBehavior) {
        this.isLoading = true;
        let promise;
        const behaviorDto = new BehaviorDto(behavior);
        if (behavior.isNew) {
            promise = this.client.createAssessmentBehavior(assessmentId, behaviorDto);
        } else {
            promise = this.client.updateAssessmentBehavior(behavior.id, assessmentId, behaviorDto);
        }
        return promise
            .then(() => {})
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public removeStep(assessmentId: string, stepId: string) {
        this.isLoading = true;
        return this.client
            .deleteAssessmentStep(stepId, assessmentId)
            .then(() => {})
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public removeBehavior(assessmentId: string, behaviorId: string) {
        this.isLoading = true;
        return this.client
            .deleteAssessmentBehavior(behaviorId, assessmentId)
            .then(() => {})
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public getAssessmentDashboardStats(
        branches: string,
        regions: string,
        assessmentId: string | undefined
    ) {
        this.isLoading = true;
        return this.client
            .getAssessmentDashboardStats(branches, regions, assessmentId)
            .then((response) => {
                this.dashboardStats = response.result;
            })
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }
}



// WEBPACK FOOTER //
// ./src/stores/AssessmentsStore/AssessmentsStore.ts